import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

type OrderItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  gst?: number; // Added for GST
  shippingFee?: number; // Added for shipping fee
};

type Order = {
  id: string;
  userId: string;
  items: OrderItem[];
  total: number;
  status: OrderStatus;
  shippingAddress: {
    name?: string;
    street?: string;
    city: string;
    state: string;
    zip?: string;
    postalCode?: string;
    country: string;
    phone?: string;
    email?: string;
    address?: string; // For backward compatibility
  };
  paymentMethod: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

export function OrdersPage() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(50) // Limit the number of orders to prevent performance issues
      );
      
      const querySnapshot = await getDocs(ordersQuery);
      const ordersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure createdAt is a Timestamp
        if (data.createdAt?.toDate) {
          data.createdAt = Timestamp.fromDate(new Date(data.createdAt.toDate()));
        }
        return {
          id: doc.id,
          ...data
        } as Order;
      });
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again.');
      console.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusBadge = (status: OrderStatus) => {
    const statusMap = {
      pending: { label: 'Pending', variant: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'Processing', variant: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Shipped', variant: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Delivered', variant: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', variant: 'bg-red-100 text-red-800' },
    };
    
    const statusInfo = statusMap[status] || { label: status, variant: 'bg-gray-100 text-gray-800' };
    return (
      <Badge className={statusInfo.variant}>
        {statusInfo.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchOrders}
          className="px-4 py-2 bg-[#FF6B35] text-white rounded hover:bg-[#E55A28] transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold mb-4">Please sign in to view your orders</h2>
        <Button onClick={() => window.location.href = '/login'}>Sign In</Button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">My Orders</h1>
          <p className="text-gray-600 mb-6">You haven't placed any orders yet.</p>
          <Button onClick={() => window.location.href = '/'}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">My Orders</h1>
        
        <div className="space-y-6">
          {orders.map((order) => (
            <Card key={order.id} className="overflow-hidden">
              <CardHeader className="bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <CardTitle className="text-lg">Order #{order.id.substring(0, 8).toUpperCase()}</CardTitle>
                    <CardDescription className="mt-1">
                      Placed on {format(order.createdAt.toDate(), 'MMMM d, yyyy')}
                    </CardDescription>
                  </div>
                  <div className="mt-2 md:mt-0">
                    {getStatusBadge(order.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Product</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-4">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                            <span>{item.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>₹{item.price.toFixed(2)}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell className="text-right">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="border-t p-4 flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="font-semibold text-gray-700 mb-2">Shipping Address:</div>
                    <div className="text-sm text-gray-600 space-y-1">
                      {order.shippingAddress?.name && (
                        <div className="font-medium">{order.shippingAddress.name}</div>
                      )}
                      {(order.shippingAddress?.street || order.shippingAddress?.address) && (
                        <div>{order.shippingAddress.street || order.shippingAddress.address}</div>
                      )}
                      <div>
                        {[
                          order.shippingAddress?.city,
                          order.shippingAddress?.state,
                          order.shippingAddress?.zip || order.shippingAddress?.postalCode
                        ].filter(Boolean).join(', ')}
                      </div>
                      {order.shippingAddress?.country && (
                        <div>{order.shippingAddress.country}</div>
                      )}
                      {order.shippingAddress?.phone && (
                        <div className="text-gray-500">Phone: {order.shippingAddress.phone}</div>
                      )}
                      {order.shippingAddress?.email && (
                        <div className="text-gray-500">Email: {order.shippingAddress.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-700 mb-2">Price Summary:</div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>₹{order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>GST:</span>
                        <span>₹{order.items.reduce((sum, item) => {
                          const itemGst = (item.price * (item.gst || 0) / 100) * item.quantity;
                          return sum + itemGst;
                        }, 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Shipping:</span>
                        <span>₹{order.items.reduce((sum, item) => {
                          return sum + (item.shippingFee || 0) * item.quantity;
                        }, 0).toFixed(2)}</span>
                      </div>
                      <div className="border-t pt-1 font-semibold">
                        <div className="flex justify-between">
                          <span>Total:</span>
                          <span>
                            ₹{order.items.reduce((sum, item) =>
                              sum + ((item.price + (item.price * (item.gst || 0) / 100) + (item.shippingFee || 0)) * item.quantity)
                            , 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 space-x-2">
                      {/* Removed View Details button */}
                      {order.status === 'pending' && (
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                          Cancel Order
                        </Button>
                      )}
                      {order.status === 'delivered' && (
                        <Button variant="outline" size="sm">
                          Buy Again
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}